import qs from 'qs';
import type { operations } from '~/clients/svc-connection-google/models';

import { json_headers, request } from '~/services/common';

import ApiError from '~/types/ApiError';
import type { TaxEstimationControl, TaxEstimationControlUpdate, TaxEstimationResponse } from '~/types/taxEstimation';

type ListPropertiesQuery = operations['listProperties']['parameters']['query'];
type ListPropertiesResponse = operations['listProperties']['responses']['200']['content']['application/json'];
type GHAPropertyResponseRow = ListPropertiesResponse['data'][number];
export type GHAProperty = GHAPropertyResponseRow;

// 'no_tier' | 'tier_1'..'tier_4'; 'no_tier' is the default (untiered).
export type CugTier = GHAProperty['cug_tier'];

export type GHAPropertyBrand = NonNullable<ListPropertiesQuery>['brand'];
export type GHAAccountType = NonNullable<ListPropertiesQuery>['accountType'];

type ListGHADiscountRulesResponse =
  operations['listGHADiscountRules']['responses']['200']['content']['application/json'];
export type GHADiscountRule = ListGHADiscountRulesResponse['result'][number];
export type GHADiscountRuleDiscountType = GHADiscountRule['discountType'];
export type GHADiscountRuleAccountType = NonNullable<
  operations['listGHADiscountRules']['parameters']['query']
>['accountType'];
export type CreateGHADiscountRuleBody =
  operations['createGHADiscountRule']['requestBody']['content']['application/json'];
export type CreateGHADiscountRuleDiscountType = NonNullable<CreateGHADiscountRuleBody['discountType']>;
export type GenerateGHADiscountTokenBody =
  operations['generateGHADiscountTokenDebug']['requestBody']['content']['application/json'];
type BulkUpdateGHADiscountRulesBody =
  operations['bulkUpdateGHADiscountRules']['requestBody']['content']['application/json'];
export type BulkUpdateGHADiscountRulesAccountType = BulkUpdateGHADiscountRulesBody['accountType'];
export type BulkUpdateGHADiscountRulesParams = BulkUpdateGHADiscountRulesBody & { file: File };
export type BulkUpdateGHADiscountRulesResponse =
  operations['bulkUpdateGHADiscountRules']['responses']['200']['content']['application/json'];
export type BulkUpdateGHADiscountRulesErrorResponse =
  operations['bulkUpdateGHADiscountRules']['responses']['400']['content']['application/json'];
export interface ListGHADiscountRulesFilters {
  propertyId?: string;
  accountType?: GHADiscountRuleAccountType;
  enabled?: boolean;
  country?: string;
  page?: number;
  limit?: number;
}

// Property List Regeneration types
type GeneratePropertyListXmlRequest =
  operations['/api/connections/google/pull/properties/generate/put']['requestBody']['content']['application/json'];
export type GHAPropertyListAccountType = GeneratePropertyListXmlRequest['accountType'];

// Property List Metadata types
type GetPropertyListMetadataQuery = operations['getPropertyListMetadata']['parameters']['query'];
type GetPropertyListMetadataResponse =
  operations['getPropertyListMetadata']['responses']['200']['content']['application/json'];
export type PropertyListMetadata = GetPropertyListMetadataResponse['result'][number];

interface PriceComponent {
  price: number;
  supplierCode: string;
  roomID?: string;
}

interface Mismatch {
  isMismatch: boolean;
  reason: string;
  difference: number;
}

export interface GHAPriceCheckRow {
  propertyId: string;
  checkIn: string;
  checkOut: string;
  occupancy: string;
  publicOffer: PriceComponent;
  metaSearch: PriceComponent;
  mismatch: Mismatch;
}

export interface GHAPriceSummary {
  mismatchCount: number;
  totalItineraries: number;
  mismatchPercentage: number;
}

interface GHAPriceDiagnoseResponse {
  results: Array<GHAPriceCheckRow>;
  summary: GHAPriceSummary;
}

export enum BulkSwitchGHAPropertiesAction {
  ENABLE = 'enable',
  DISABLE = 'disable',
}

function getBaseURL() {
  return window.configs.API_HOST + '/api';
}

export async function ghaPriceDiagnoseQuery({
  propertyIds,
  checkIn,
  nights,
  isMobile,
  combinations,
  occupancy,
}: {
  propertyIds: string;
  checkIn: string;
  nights: number;
  isMobile: boolean;
  combinations: number;
  occupancy: number;
}): Promise<{ result: GHAPriceDiagnoseResponse }> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/meta-search/query/diagnose?`;

  const body = {
    propertyIds: propertyIds.length > 0 ? propertyIds.split(',') : [],
    checkIn,
    nights,
    isMobile,
    combinations,
    occupancy,
  };

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

export async function ghaUploadNewProperty(file: File) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties`;

  const formData = new FormData();
  formData.append('file', file);

  return request(url, {
    method: 'POST',
    body: formData,
    headers: {
      Accept: 'application/json',
    },
  });
}

export enum ChannelManager {
  TRAVELCLICK = 'travelclick',
  SITEMINDER = 'siteminder',
  SELF_MANAGED = 'self-managed',
  BEDBANK = 'bedbank',
  DERBYSOFT = 'derbysoft',
  SYNXIS = 'synxis',
  SHIJI = 'shiji',
}

export enum GHAPropertyType {
  VACATION_RENTAL = 'vacation_rental',
  HOTEL = 'hotel',
}

// Account types for property list generation by brand
export enum LEPropertyListAccountType {
  LPP = 'lpp',
  LE_VACATION_RENTAL = 'le_vacation_rental',
}

export enum ScooponPropertyListAccountType {
  SCOOPON = 'scoopon',
}

export interface SearchGHAPropertiesResponse {
  results: Array<GHAProperty>;
  total: number;
}

export interface SearchGHAPropertiesParams {
  propertyIds: Array<string>;
  liveOnGoogle?: boolean;
  accountType?: GHAAccountType;
  blacklisted?: boolean;
  discountConfigured?: boolean;
  discountBlacklisted?: boolean;
  channelManagers: Array<ChannelManager>;
  page: number;
  limit: number;
  name?: string;
  countryCodes?: Array<string>;
  ghaPropertyTypes?: Array<GHAPropertyType>;
  brand?: GHAPropertyBrand;
  distanceGroups?: Array<string>;
  chain?: string;
  cugTiers?: Array<CugTier>;
  // Only honoured alongside an accountType — the API rejects it with a 400 otherwise, since a
  // classification tier belongs to one account.
  classificationTiers?: Array<GHAPropertyClassificationTier>;
}

export async function searchGHAProperties({
  propertyIds,
  liveOnGoogle,
  accountType,
  blacklisted,
  discountConfigured,
  discountBlacklisted,
  channelManagers,
  name,
  countryCodes,
  ghaPropertyTypes,
  brand,
  distanceGroups,
  page,
  limit,
  chain,
  cugTiers,
  classificationTiers,
}: SearchGHAPropertiesParams): Promise<SearchGHAPropertiesResponse> {
  const baseURL = getBaseURL();
  const queryParams = {
    propertyIds,
    channelManagers,
    page: page.toString(),
    limit: limit.toString(),
    liveOnGoogle: liveOnGoogle !== undefined ? liveOnGoogle.toString() : undefined,
    accountType,
    blacklisted: blacklisted !== undefined ? blacklisted.toString() : undefined,
    discountConfigured: discountConfigured !== undefined ? discountConfigured.toString() : undefined,
    discountBlacklisted: discountBlacklisted !== undefined ? discountBlacklisted.toString() : undefined,
    name,
    countryCodes,
    ghaPropertyTypes,
    brand,
    distanceGroups,
    chain,
    cugTiers,
    classificationTiers,
  };

  // 'repeat' => repeated keys (cugTiers=tier_1&cugTiers=tier_4) per backend contract
  const url = `${baseURL}/connections/google/listProperties?${qs.stringify(queryParams, {
    arrayFormat: 'repeat',
    skipNulls: true,
  })}`;

  const response = await request(url, {
    method: 'GET',
  });

  return {
    results: response.data || [],
    total: response.total || 0,
  };
}

/**
 * Chain names present in the GHA property data, for the chain filter typeahead. Returns the whole
 * list (~5k) so the client filters locally; pass the selected tiers to narrow it.
 */
export async function getGHAChains(cugTiers?: Array<CugTier>): Promise<Array<string>> {
  const baseURL = getBaseURL();

  // 'repeat' => repeated keys (cugTiers=tier_3&cugTiers=tier_4) per backend contract
  const query = qs.stringify({ cugTiers }, { arrayFormat: 'repeat', skipNulls: true });
  const url = `${baseURL}/connections/google/properties/chains${query ? `?${query}` : ''}`;

  const response = await request(url, { method: 'GET' });

  return response.result || [];
}

export async function disableGHAProperty(propertyIds: Array<string>, accountType: GHAAccountType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/disable`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds, accountType }),
  });
}

export async function enableGHAProperty(propertyIds: Array<string>, accountType: GHAAccountType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/enable`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds, accountType }),
  });
}

interface BulkSwitchGHAPropertiesParams {
  file: File;
  action: BulkSwitchGHAPropertiesAction;
  accountType: GHAAccountType;
}

export async function bulkSwitchGHAProperties({ file, action, accountType }: BulkSwitchGHAPropertiesParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/${action}`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountType', accountType);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

interface UpdatePropertiesParams {
  propertyIds: Array<string>;
  field: string;
  value: boolean;
}
interface BulkAppendPricingRegionsParams {
  file: File;
  accountType: GHAAccountType;
}

export async function bulkAppendPricingRegions({ file, accountType }: BulkAppendPricingRegionsParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/pricing-regions`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountType', accountType);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

export async function bulkUpdateGHADiscountRules({
  file,
  accountType,
}: BulkUpdateGHADiscountRulesParams): Promise<BulkUpdateGHADiscountRulesResponse> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/discount-rules/bulk`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountType', accountType);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

export async function updatePropertyPricingRegions(propertyIds: Array<string>, pricingRegions: Array<string>) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties`;

  const body = propertyIds.map((id) => ({
    id,
    pricing_regions: pricingRegions,
  }));

  return request(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    ignoreResponse: true,
  });
}

export async function updateProperties(props: UpdatePropertiesParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties`;

  const body = props.propertyIds.map((id) => ({
    id,
    [props.field]: props.value,
  }));

  return request(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    ignoreResponse: true,
  });
}

export async function getGHAAggregateReport({
  aggregateNumber,
  sortBy,
  sortOrder,
  page,
  limit,
}: {
  aggregateNumber: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
}): Promise<{ result; count: number }> {
  const queryParams = {
    sortBy,
    sortOrder,
    aggregateNumber,
    page,
    limit,
  };
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/reports/aggregate-report?${qs.stringify(queryParams)}`;
  return request(url, {
    method: 'GET',
  });
}

export function downloadRecentPricingReport() {
  const baseURL = getBaseURL();
  const downloadUrl = `${baseURL}/connections/google/reports/pricing/all`;
  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

export function downloadGHAAggregateReportCSV({
  aggregateNumber,
  sortBy,
  sortOrder,
}: {
  aggregateNumber: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}) {
  const queryParams = {
    sortBy,
    sortOrder,
    aggregateNumber,
  };
  const baseURL = getBaseURL();
  const downloadUrl = `${baseURL}/connections/google/reports/aggregate-report.csv?${qs.stringify(queryParams)}`;
  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

export function downloadGHARawReportCSV({
  propertyIds,
  createdAtStart,
  createdAtEnd,
  limit,
}: {
  propertyIds?: Array<string>;
  createdAtStart?: string;
  createdAtEnd?: string;
  limit?: string;
}) {
  const queryParams = {
    propertyId: propertyIds && propertyIds.length > 0 ? propertyIds : undefined,
    createdAtStart,
    createdAtEnd,
    limit,
  };
  const queryString = qs.stringify(queryParams, { arrayFormat: 'repeat', skipNulls: true });
  const baseURL = getBaseURL();
  const downloadUrl = `${baseURL}/connections/google/reports/report.csv?${queryString}`;

  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

export function downloadGHAPropertiesCSV(params: Omit<SearchGHAPropertiesParams, 'page' | 'limit'>) {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true });
  const downloadUrl = `${baseURL}/connections/google/properties/export/csv?${queryString}`;
  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

export async function enableGHPriceTracking(propertyIds: Array<string>) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/price-tracking/enable`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds }),
  });
}

export async function disableGHPriceTracking(propertyIds: Array<string>) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/price-tracking/disable`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds }),
  });
}

export async function updateGHAPropertyType(propertyIds: Array<string>, ghaPropertyType: GHAPropertyType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/update/gha-property-type`;

  return request(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds, ghaPropertyType }),
  });
}

export async function bulkUpdateGHAPropertyType(file: File, ghaPropertyType: GHAPropertyType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/update/gha-property-type`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('ghaPropertyType', ghaPropertyType);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

export enum BlacklistAction {
  BLACKLIST = 'blacklist',
  UNBLACKLIST = 'unblacklist',
}

export async function updatePropertyBlacklist(propertyIds: Array<string>, action: BlacklistAction) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/blacklist/${action}`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds }),
  });
}

interface BulkBlacklistPropertiesParams {
  file: File;
  action: BlacklistAction;
}

export async function bulkBlacklistProperties({ file, action }: BulkBlacklistPropertiesParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/blacklist/${action}`;

  const formData = new FormData();
  formData.append('file', file);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

export enum CUGWhitelistAction {
  WHITELIST = 'whitelist',
  UNWHITELIST = 'unwhitelist',
}

interface BulkCUGWhitelistPropertiesParams {
  file: File;
  action: CUGWhitelistAction;
}

export async function bulkCUGWhitelistProperties({ file, action }: BulkCUGWhitelistPropertiesParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/cug-whitelist`;

  const whitelisted = action === CUGWhitelistAction.WHITELIST;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('whitelisted', whitelisted.toString());

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

// CUG Tier: JSON PUT for selected property ids, multipart CSV POST for bulk uploads.
interface SetCugTierParams {
  propertyIds: Array<string>;
  tier: CugTier;
}

export async function setCugTier({ propertyIds, tier }: SetCugTierParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/cug-tier`;

  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ propertyIds, tier }),
  });
}

interface BulkSetCugTierParams {
  file: File;
  // Tier applied to every row in the CSV.
  tier: CugTier;
}

export async function bulkSetCugTier({ file, tier }: BulkSetCugTierParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/cug-tier`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('tier', tier);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

// Property List Regeneration
export async function generatePropertyListXml(accountType: GHAPropertyListAccountType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/pull/properties/generate`;

  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ accountType }),
  });
}

// Property List Metadata
export async function getPropertyListMetadata(
  params?: GetPropertyListMetadataQuery,
): Promise<GetPropertyListMetadataResponse> {
  const baseURL = getBaseURL();
  const queryString = params ? `?${qs.stringify(params)}` : '';
  const url = `${baseURL}/connections/google/properties/list/metadata${queryString}`;

  return request(url, {
    method: 'GET',
  });
}

export async function listGHADiscountRules(
  filters: ListGHADiscountRulesFilters = {},
  options?: { signal?: AbortSignal },
): Promise<ListGHADiscountRulesResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(
    {
      ...filters,
      enabled: filters.enabled === undefined ? undefined : String(filters.enabled),
    },
    { skipNulls: true },
  );
  const url = `${baseURL}/connections/google/discount-rules${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

export async function createGHADiscountRule(
  input: CreateGHADiscountRuleBody,
): Promise<operations['createGHADiscountRule']['responses']['201']['content']['application/json']> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/discount-rules`;

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function generateGHADiscountTokenDebug(
  input: GenerateGHADiscountTokenBody,
): Promise<operations['generateGHADiscountTokenDebug']['responses']['200']['content']['application/json']> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/debug/generate-discount-token`;

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function archiveGHADiscountRule(
  id: string,
): Promise<operations['archiveGHADiscountRule']['responses']['200']['content']['application/json']> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/discount-rules/${id}/archive`;

  return request(url, {
    method: 'PATCH',
    headers: json_headers,
  });
}

type HintCoverageMatrixResponse =
  operations['getHintCoverageMatrix']['responses']['200']['content']['application/json'];
export type HintCoverageMatrixResult = HintCoverageMatrixResponse['result'];
export type HintCoverageMatrixCell = HintCoverageMatrixResult['matrix'][number];
export type HintCoverageMatrixParams = Partial<operations['getHintCoverageMatrix']['parameters']['query']> &
  Pick<operations['getHintCoverageMatrix']['parameters']['query'], 'accountType'>;

export async function getHintCoverageMatrix(
  params: HintCoverageMatrixParams,
): Promise<{ result: HintCoverageMatrixResult }> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  const url = `${baseURL}/connections/google/reports/hint-coverage?${queryString}`;

  return request(url, {
    method: 'GET',
  });
}

type HintCoverageGridResponse = operations['getHintCoverageGrid']['responses']['200']['content']['application/json'];
export type HintCoverageGridResult = HintCoverageGridResponse['result'];
export type HintCoverageGridCell = HintCoverageGridResult['cells'][number];
export type HintCoverageAccountType = HintCoverageMatrixParams['accountType'];
export type HintCoverageGridParams = operations['getHintCoverageGrid']['parameters']['query'];

export async function getHintCoverageGrid(
  params: HintCoverageGridParams,
  options?: { signal?: AbortSignal },
): Promise<{ result: HintCoverageGridResult }> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  const url = `${baseURL}/connections/google/reports/hint-coverage/buckets?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

type AllocatePropertiesForHintsResponse =
  operations['allocatePropertiesForHints']['responses']['202']['content']['application/json'];

export async function allocateHintProperties(
  accountType: HintCoverageAccountType,
): Promise<AllocatePropertiesForHintsResponse> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/hints/allocate-properties`;

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ accountType }),
  });
}

type ListHintPropertiesResponse = operations['listHintProperties']['responses']['200']['content']['application/json'];
export type AllocatedProperty = ListHintPropertiesResponse['data'][number];
export type AllocatedPropertyTier = AllocatedProperty['tier'];
export type GetAllocatedPropertiesParams = operations['listHintProperties']['parameters']['query'];

export async function getHintAllocatedProperties(
  params: GetAllocatedPropertiesParams,
  options?: { signal?: AbortSignal },
): Promise<ListHintPropertiesResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify({ ...params, search: params.search || undefined }, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/properties?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

type PropertyHintCoverageResponse =
  operations['getPropertyHintCoverage']['responses']['200']['content']['application/json'];
export type PropertyHintCoverageRow = PropertyHintCoverageResponse['rows'][number];
export type GetPropertyHintCoverageParams = operations['getPropertyHintCoverage']['parameters']['query'];

export async function getPropertyHintCoverage(
  propertyId: string,
  params: GetPropertyHintCoverageParams,
  options?: { signal?: AbortSignal },
): Promise<PropertyHintCoverageResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/properties/${propertyId}/coverage?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

// Hint refresh queue (GR-4819): the next hint cycle replays every itinerary Google currently
// caches for enqueued properties, prompting Google to re-pull fresh prices. The queue is
// ephemeral — entries disappear once refreshed, so the GET is a live view, not a history.
export type HintRefreshEnqueueResult =
  operations['enqueueHintRefresh']['responses']['200']['content']['application/json']['result'];
type HintRefreshQueueResponse = operations['getHintRefreshQueue']['responses']['200']['content']['application/json'];
export type HintRefreshQueueEntry = HintRefreshQueueResponse['result']['queue'][number];
export type HintRefreshRemoveResult =
  operations['removeHintRefresh']['responses']['200']['content']['application/json']['result'];

export async function enqueueHintRefresh(
  propertyIds: Array<string>,
  accountType: GHAAccountType,
): Promise<HintRefreshEnqueueResult> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/hints/properties/refresh`;

  const response = await request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds, accountType }),
  });
  return response.result;
}

export async function getHintRefreshQueue(
  accountType: GHAAccountType,
  options?: { signal?: AbortSignal },
): Promise<Array<HintRefreshQueueEntry>> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify({ accountType }, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/properties/refresh?${queryString}`;

  const response: HintRefreshQueueResponse = await request(url, {
    method: 'GET',
    signal: options?.signal,
  });
  return response.result.queue;
}

export async function removeHintRefresh(
  propertyIds: Array<string>,
  accountType: GHAAccountType,
): Promise<HintRefreshRemoveResult> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/hints/properties/refresh`;

  const response = await request(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds, accountType }),
  });
  return response.result;
}

interface BulkEnqueueHintRefreshParams {
  file: File;
  accountType: GHAAccountType;
}

/** CSV variant of enqueueHintRefresh: single propertyIds column, max 500 rows. */
export async function bulkEnqueueHintRefresh({
  file,
  accountType,
}: BulkEnqueueHintRefreshParams): Promise<HintRefreshEnqueueResult> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/hints/properties/refresh/bulk`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountType', accountType);

  const response = await request(url, {
    method: 'POST',
    body: formData,
  });
  return response.result;
}

export type ExportHintPropertiesCSVParams = operations['exportHintPropertiesCSV']['parameters']['query'];

export function downloadHintAllocatedPropertiesCSV(params: ExportHintPropertiesCSVParams) {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  const downloadUrl = `${baseURL}/connections/google/hints/properties/export/csv?${queryString}`;

  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

type ListHintRegionSettingsResponse =
  operations['listHintRegionSettings']['responses']['200']['content']['application/json'];
export type HintRegionSetting = ListHintRegionSettingsResponse['result'][number];
export type UpsertHintRegionSettingBody =
  operations['upsertHintRegionSetting']['requestBody']['content']['application/json'];
type UpsertHintRegionSettingResponse =
  operations['upsertHintRegionSetting']['responses']['200']['content']['application/json'];

export async function listHintRegionSettings(
  accountType: HintCoverageAccountType,
  options?: { signal?: AbortSignal },
): Promise<ListHintRegionSettingsResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify({ accountType }, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/region-settings?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

// Persist-only: the backend queues nothing on save — a settings change takes effect at
// the next weekly reclassification cron or a Re-classify Properties click.
export async function upsertHintRegionSetting(
  body: UpsertHintRegionSettingBody,
): Promise<UpsertHintRegionSettingResponse> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/hints/region-settings`;

  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

type HintRegionCompositionResponse =
  operations['getHintRegionComposition']['responses']['200']['content']['application/json'];
export type HintRegionComposition = HintRegionCompositionResponse['result'];
export type HintRegionCompositionRow = HintRegionComposition['regions'][number];

export async function getHintRegionComposition(
  accountType: HintCoverageAccountType,
  options?: { signal?: AbortSignal },
): Promise<HintRegionCompositionResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify({ accountType }, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/region-composition?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

type ListHintClassificationChangesResponse =
  operations['listHintClassificationChanges']['responses']['200']['content']['application/json'];
export type ClassificationChange = ListHintClassificationChangesResponse['data'][number];
export type ClassificationChangeTier = ClassificationChange['oldTier'];
export type GetClassificationChangesParams = operations['listHintClassificationChanges']['parameters']['query'];

export async function getHintClassificationChanges(
  params: GetClassificationChangesParams,
  options?: { signal?: AbortSignal },
): Promise<ListHintClassificationChangesResponse> {
  const baseURL = getBaseURL();
  // 'repeat' => repeated keys (oldTier=HIGH&oldTier=MEDIUM) per backend contract, same as
  // cugTiers on listProperties. The default indexed form is silently ignored by the API.
  const queryString = qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true });
  const url = `${baseURL}/connections/google/hints/classification-changes?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

// GHA-442 property discount summary. Hand-written provisional types — the endpoint is not in the
// deployed swagger yet; swap to the generated operations types once svc-connection-google merges
// and models.d.ts is regenerated (same interim pattern as GHAPriceCheckRow above).
export interface GHADiscountSummaryLine {
  /** ISO-2 viewer country, or null for a universal rule (applies to every enabled country). */
  country: string | null;
  discountType: 'percentage' | 'margin_retention';
  discountValue: number;
  propertyCount: number;
  byTier: Record<string, number>;
}

export interface GHAExclusionRuleLine {
  // Multi-value shape from the expanded summary endpoint; optional so the singular legacy shape
  // below keeps rendering until that endpoint deploys.
  suppliers?: Array<string> | null;
  supplierNames?: Array<string> | null;
  /** Legacy singular shape the deployed summary still serves; remove once the plural fields ship. */
  supplier?: string | null;
  supplierName?: string | null;
  cugTiers: Array<string> | null;
  countries: Array<string> | null;
  chainNames: Array<string | null> | null;
  // GHA-429 additive fields (table-backed exclusions); optional until the expanded
  // discount-summary endpoint is deployed in svc-connection-google.
  id?: string;
  enabled?: boolean;
  /** Live properties whose chain/tier the veto touches — leg-level qualifiers don't narrow it. */
  affectedLiveProperties?: number;
}

/** GHA-429: every live property in exactly one bucket, resolver-precedence order; sums to live. */
export interface GHACoverageBuckets {
  live: number;
  blacklisted: number;
  fullyExcluded: number;
  propertyRuleDiscounted: number;
  scopeRuleDiscounted: number;
  undiscounted: number;
}

/** GHA-429: properties a scope rule actually wins for one viewer country (overlapping rules
 *  never double-count — the highest-priority match claims the property). A rule with no country
 *  list appears once per landing country with the same ruleId — never sum counts across
 *  countries. */
export interface GHAScopedDiscountLine {
  ruleId: string;
  priority: number;
  country: string;
  allCountries: boolean;
  discountType: 'percentage' | 'margin_retention';
  discountValue: number;
  scope: {
    suppliers: Array<string> | null;
    supplierNames: Array<string> | null;
    chainNames: Array<string> | null;
    cugTiers: Array<string> | null;
  };
  propertyCount: number;
  byTier: Record<string, number>;
}

export interface GHAAccountDiscountSummary {
  accountType: string;
  liveProperties: { total: number; byTier: Record<string, number> };
  ruleCoordinates: Array<{ minAdvanceBookingWindow: number; minLengthOfStay: number }>;
  discounts: Array<GHADiscountSummaryLine>;
  exclusionRules: Array<GHAExclusionRuleLine>;
  // GHA-429 additive fields; optional until the expanded discount-summary endpoint is deployed.
  coverage?: GHACoverageBuckets;
  scopedDiscounts?: Array<GHAScopedDiscountLine>;
  /** False = scope rules are configured but not emitting to Google yet. */
  scopedRulesLive?: boolean;
}

// property_classification tier (Pull-with-Hints HIGH/MEDIUM/LOW), unrelated to cug_tier.
export type GHAPropertyClassificationTier = 'HIGH' | 'MEDIUM' | 'LOW';

export interface GHADiscountSummaryFilters {
  classificationTiers?: Array<GHAPropertyClassificationTier>;
}

export async function getGHADiscountSummary(
  filters: GHADiscountSummaryFilters = {},
  options?: { signal?: AbortSignal },
): Promise<{
  status: number;
  message: string;
  result: Array<GHAAccountDiscountSummary>;
}> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(
    { classificationTiers: filters.classificationTiers },
    { arrayFormat: 'repeat', skipNulls: true },
  );
  return request(`${baseURL}/connections/google/discount-summary${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

// CUG rate leaking summary. Hand-written provisional types, same interim pattern as the discount
// summary above — swap to the generated operations types once svc-connection-google merges and
// models.d.ts is regenerated.
export interface GHACugLeakTierLine {
  cugTier: string;
  /** never = no CUG whatever the whitelist says; smart = window-gated; always = CUG 24/7. */
  policy: 'never' | 'smart' | 'always';
  liveTotal: number;
  whitelisted: number;
  leakingAllViewers: number;
  leakingInternationalOnly: number;
  missingTimezone: number;
}

export interface GHALeakWindow {
  /** Luxon weekdays, 1 = Monday. */
  days: Array<number>;
  startHour: number;
  endHour: number;
}

export interface GHAAccountCugLeakSummary {
  accountType: string;
  configured: boolean;
  enabled: boolean;
  smartLeakWindows: Array<GHALeakWindow>;
  byTier: Array<GHACugLeakTierLine>;
}

export async function getCugLeakSummary(
  filters: GHADiscountSummaryFilters = {},
  options?: { signal?: AbortSignal },
): Promise<{
  status: number;
  message: string;
  result: Array<GHAAccountCugLeakSummary>;
}> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(
    { classificationTiers: filters.classificationTiers },
    { arrayFormat: 'repeat', skipNulls: true },
  );
  return request(`${baseURL}/connections/google/properties/cug-leak-summary${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

// ---------------------------------------------------------------------------
// GHA-428/429 scoped discount rules and exclusions.
//
// Hand-written provisional types — the same interim pattern as the discount summary above: swap
// to the generated `operations` types once the svc-connection-google GHA-429 endpoints are
// deployed and models.d.ts is regenerated.
// ---------------------------------------------------------------------------

// The scoped tables never speak no_tier: the backend folds NoTier/unclassified to tier_1.
export type GHAScopeRuleCugTier = 'tier_1' | 'tier_2' | 'tier_3' | 'tier_4';
export type GHAScopeRuleDiscountType = 'percentage' | 'margin_retention';

export interface GHADiscountScopeRule {
  id: string;
  accountType: GHADiscountRuleAccountType;
  suppliers: Array<string> | null;
  chainNames: Array<string> | null;
  cugTiers: Array<GHAScopeRuleCugTier> | null;
  userCountries: Array<string> | null;
  discountType: GHAScopeRuleDiscountType;
  discountValue: number;
  priority: number;
  minAdvanceBookingWindow: number;
  minLengthOfStay: number;
  enabled: boolean;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Omitted dimension = matches all; a listed dimension is OR within it, AND across dimensions.
 *  Empty arrays are rejected by the API — omit the key instead. */
export interface GHADiscountScopeRuleBody {
  accountType: GHADiscountRuleAccountType;
  suppliers?: Array<string>;
  chainNames?: Array<string>;
  cugTiers?: Array<GHAScopeRuleCugTier>;
  userCountries?: Array<string>;
  discountType: GHAScopeRuleDiscountType;
  discountValue: number;
  priority: number;
  minAdvanceBookingWindow?: number;
  minLengthOfStay?: number;
}

export interface GHADiscountExclusion {
  id: string;
  accountType: GHADiscountRuleAccountType;
  suppliers: Array<string> | null;
  chainNames: Array<string> | null;
  cugTiers: Array<GHAScopeRuleCugTier> | null;
  userCountries: Array<string> | null;
  enabled: boolean;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GHADiscountExclusionBody {
  accountType: GHADiscountRuleAccountType;
  suppliers?: Array<string>;
  chainNames?: Array<string>;
  cugTiers?: Array<GHAScopeRuleCugTier>;
  userCountries?: Array<string>;
}

/** Explicit null clears a dimension back to match-all (true in-place edit, unlike grants). */
export interface GHADiscountExclusionPatchBody {
  suppliers?: Array<string> | null;
  chainNames?: Array<string> | null;
  cugTiers?: Array<GHAScopeRuleCugTier> | null;
  userCountries?: Array<string> | null;
  enabled?: boolean;
}

export interface ListGHAScopedRulesFilters {
  accountType?: GHADiscountRuleAccountType;
  enabled?: boolean;
  page?: number;
  limit?: number;
}

const scopedRuleQueryString = (filters: ListGHAScopedRulesFilters): string => {
  const query = qs.stringify(
    {
      accountType: filters.accountType,
      enabled: filters.enabled == null ? undefined : String(filters.enabled),
      page: filters.page,
      limit: filters.limit,
    },
    { skipNulls: true },
  );
  return query ? `?${query}` : '';
};

export async function listGHADiscountScopeRules(
  filters: ListGHAScopedRulesFilters = {},
  options?: { signal?: AbortSignal },
): Promise<{ status: number; message: string; total: number; result: Array<GHADiscountScopeRule> }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/discount-scope-rules${scopedRuleQueryString(filters)}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

export async function createGHADiscountScopeRule(
  input: GHADiscountScopeRuleBody,
): Promise<{ status: number; message: string; result: GHADiscountScopeRule }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/discount-scope-rules`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

/** Grants are immutable — "edit" archives the rule and creates its replacement atomically. */
export async function replaceGHADiscountScopeRule(
  id: string,
  input: GHADiscountScopeRuleBody,
): Promise<{ status: number; message: string; result: GHADiscountScopeRule }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/discount-scope-rules/${id}/replace`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function archiveGHADiscountScopeRule(id: string): Promise<{ status: number; message: string }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/discount-scope-rules/${id}/archive`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify({}),
  });
}

/** The FULL intended ordering of the account's active rules (not a delta); first = wins. */
export async function reorderGHADiscountScopeRules(input: {
  accountType: GHADiscountRuleAccountType;
  orderedIds: Array<string>;
}): Promise<{ status: number; message: string }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/discount-scope-rules/reorder`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function listGHADiscountExclusions(
  filters: ListGHAScopedRulesFilters = {},
  options?: { signal?: AbortSignal },
): Promise<{ status: number; message: string; total: number; result: Array<GHADiscountExclusion> }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/discount-exclusion-rules${scopedRuleQueryString(filters)}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

export async function createGHADiscountExclusion(
  input: GHADiscountExclusionBody,
): Promise<{ status: number; message: string; result: GHADiscountExclusion }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/discount-exclusion-rules`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function updateGHADiscountExclusion(
  id: string,
  input: GHADiscountExclusionPatchBody,
): Promise<{ status: number; message: string; result: GHADiscountExclusion }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/discount-exclusion-rules/${id}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// GHA-429 effective-discount explain endpoints: "what would this property emit,
// and which rule answers each (supplier x viewer country) leg?" Resolved by the
// backend through the exact emission path, so the answer can't drift from what
// Google is served. Hand-written provisional types, same interim pattern as the
// scoped rules above.
// ---------------------------------------------------------------------------

export type GHAEffectiveDiscountOutcome = 'discounted' | 'blocked' | 'none';
export type GHAEffectiveDiscountSource = 'property' | 'scope_rule' | 'exclusion' | 'blacklist';

export interface GHAEffectiveDiscountScope {
  suppliers: Array<string> | null;
  supplierNames: Array<string> | null;
  chainNames: Array<string> | null;
  cugTiers: Array<string> | null;
  userCountries: Array<string> | null;
  priority: number | null;
}

export interface GHAEffectiveDiscountLeg {
  /** null = the supplier-less rates some feeds carry. */
  supplier: string | null;
  supplierName: string | null;
  /** Viewer country, including the ZZ world leg. */
  userCountry: string;
  outcome: GHAEffectiveDiscountOutcome;
  /** Which layer answered; null when the outcome is 'none'. */
  source: GHAEffectiveDiscountSource | null;
  /** Winning or blocking rule id; null for blacklist and 'none'. */
  ruleId: string | null;
  discountType: GHAScopeRuleDiscountType | null;
  discountValue: number | null;
  /** Why a leg is full price; null unless the outcome is 'none'. */
  reason: 'world_leg' | 'no_scope_rule_matched' | null;
  /** Criteria of the winning/blocking scoped rule; null for property/blacklist sources. */
  scope: GHAEffectiveDiscountScope | null;
}

/** The itinerary the resolution assumed (defaults: 30 days out, 1 night); echoed for display. */
export interface GHAEffectiveDiscountItinerary {
  checkIn: string;
  nights: number;
}

export interface GHAPropertyEffectiveDiscount {
  propertyId: string;
  /** False = the account doesn't serve this property (not a member, or not live on Google);
   *  the legs are still a truthful preview of what it would emit. */
  liveForAccount: boolean;
  abw: number | null;
  legs: Array<GHAEffectiveDiscountLeg>;
}

export interface GHAEffectiveDiscountResult {
  accountType: string;
  /** False = scope rules resolve here as a preview but are not emitting to Google yet. */
  scopedRulesLive: boolean;
  itinerary: GHAEffectiveDiscountItinerary;
  property: GHAPropertyEffectiveDiscount;
}

export async function getGHAEffectiveDiscount(
  propertyId: string,
  params: { accountType: GHAAccountType; checkIn?: string; nights?: number },
  options?: { signal?: AbortSignal },
): Promise<{ status: number; message: string; result: GHAEffectiveDiscountResult }> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  return request(`${baseURL}/connections/google/effective-discounts/${propertyId}?${queryString}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

// ---------------------------------------------------------------------------
// GHA-403 email domain rules: one classification per email domain, read by the voucher mint's
// email classification. Protected webmail domains (gmail.com, outlook.com, ...) can only be
// consumer; the API answers 400 with a message otherwise.
// ---------------------------------------------------------------------------

export type GHAEmailClassification = 'consumer' | 'hotel' | 'uncertain';

export interface GHAEmailDomainRule {
  domain: string;
  classification: GHAEmailClassification;
  reason: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListGHAEmailDomainRulesFilters {
  classification?: GHAEmailClassification;
  /** Case-insensitive substring of the domain. */
  domain?: string;
  page?: number;
  limit?: number;
}

export interface UpsertGHAEmailDomainRuleBody {
  classification: GHAEmailClassification;
  reason?: string;
}

export interface BulkUpsertGHAEmailDomainRulesResult {
  created: number;
  updated: number;
}

export async function listGHAEmailDomainRules(
  filters: ListGHAEmailDomainRulesFilters = {},
  options?: { signal?: AbortSignal },
): Promise<{ status: number; message: string; total: number; result: Array<GHAEmailDomainRule> }> {
  const baseURL = getBaseURL();
  const query = qs.stringify(filters, { skipNulls: true });
  return request(`${baseURL}/connections/google/email-domain-rules${query ? `?${query}` : ''}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

export async function upsertGHAEmailDomainRule(
  domain: string,
  input: UpsertGHAEmailDomainRuleBody,
): Promise<{ status: number; message: string; result: GHAEmailDomainRule }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/email-domain-rules/${encodeURIComponent(domain)}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function deleteGHAEmailDomainRule(domain: string): Promise<{ status: number; message: string }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/email-domain-rules/${encodeURIComponent(domain)}`, {
    method: 'DELETE',
  });
}

export async function bulkUpsertGHAEmailDomainRules(
  file: File,
): Promise<{ status: number; message: string; result: BulkUpsertGHAEmailDomainRulesResult }> {
  const baseURL = getBaseURL();
  const formData = new FormData();
  formData.append('file', file);
  return request(`${baseURL}/connections/google/email-domain-rules/bulk`, {
    method: 'POST',
    body: formData,
  });
}

// ---------------------------------------------------------------------------
// GHA-403 blocked identities: specific people an admin has blocked from GHA discounting, at every
// property. The API stores each address canonical (lower-case; plus tag, Gmail dots and Yahoo `-`
// suffix removed), so any variant of an address finds the same row. An address never goes in a URL:
// one row is addressed by its id, and create and search send the address in the body.
// ---------------------------------------------------------------------------

export type GHABlockedIdentityKind = 'email';

/** What led to the block, for the reader. The classifier never matches on it. */
export interface GHABlockedIdentityEvidence {
  name?: string;
  phone?: string;
  ip?: string;
  bookingId?: string;
  propertyId?: string;
  hotel?: string;
  currency?: string;
  region?: string;
  caseRef?: string;
  raisedBy?: string;
}

export interface GHABlockedIdentity {
  id: string;
  kind: GHABlockedIdentityKind;
  value: string;
  reason: string | null;
  evidence: GHABlockedIdentityEvidence | null;
  source: 'manual';
  expiresAt: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListGHABlockedIdentitiesFilters {
  kind?: GHABlockedIdentityKind;
  /** Case-insensitive substring of the address. */
  value?: string;
  /** Expired rows are hidden unless asked for. */
  includeExpired?: boolean;
  page?: number;
  limit?: number;
}

/** Every field is replaced on a save, so an edit must send all three, `null` to clear one. */
export interface GHABlockedIdentityDetails {
  reason: string | null;
  evidence: GHABlockedIdentityEvidence | null;
  expiresAt: string | null;
}

export interface BulkUpsertGHABlockedIdentitiesResult {
  created: number;
  updated: number;
}

export async function listGHABlockedIdentities(
  filters: ListGHABlockedIdentitiesFilters = {},
  options?: { signal?: AbortSignal },
): Promise<{ status: number; message: string; total: number; result: Array<GHABlockedIdentity> }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/blocked-identities/search`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(filters),
    signal: options?.signal,
  });
}

/** Create, or overwrite the row already holding the same canonical address. */
export async function createGHABlockedIdentity(
  kind: GHABlockedIdentityKind,
  value: string,
  details: GHABlockedIdentityDetails,
): Promise<{ status: number; message: string; result: GHABlockedIdentity }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/blocked-identities`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ kind, value, ...details }),
  });
}

export async function updateGHABlockedIdentity(
  id: string,
  details: GHABlockedIdentityDetails,
): Promise<{ status: number; message: string; result: GHABlockedIdentity }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/blocked-identities/${id}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(details),
  });
}

export async function deleteGHABlockedIdentity(id: string): Promise<{ status: number; message: string }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/blocked-identities/${id}`, {
    method: 'DELETE',
  });
}

export async function bulkUpsertGHABlockedIdentities(
  file: File,
): Promise<{ status: number; message: string; result: BulkUpsertGHABlockedIdentitiesResult }> {
  const baseURL = getBaseURL();
  const formData = new FormData();
  formData.append('file', file);
  return request(`${baseURL}/connections/google/blocked-identities/bulk`, {
    method: 'POST',
    body: formData,
  });
}

// ---------------------------------------------------------------------------
// GHA-438 CUG tier chain rules: bedbank chain names whose properties the daily CUG tier job raises to
// Tier 1 or Tier 2. The API accepts only tier_1 and tier_2; a row is addressed by id because chain
// names contain spaces and '&'.
// ---------------------------------------------------------------------------

export type GHACugTierChainRuleTier = Extract<CugTier, 'tier_1' | 'tier_2'>;

export interface GHACugTierChainRule {
  id: string;
  chainName: string;
  cugTier: CugTier;
  reason: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertGHACugTierChainRuleBody {
  chainName: string;
  cugTier: GHACugTierChainRuleTier;
  /** Replaced on every save; omitted clears it. */
  reason?: string;
}

export async function listGHACugTierChainRules(options?: {
  signal?: AbortSignal;
}): Promise<{ status: number; message: string; result: Array<GHACugTierChainRule> }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/cug-tier-chain-rules`, { method: 'GET', signal: options?.signal });
}

/** Adds the chain, or changes its tier when the same name (ignoring case and spaces) is already listed. */
export async function upsertGHACugTierChainRule(
  input: UpsertGHACugTierChainRuleBody,
): Promise<{ status: number; message: string; result: GHACugTierChainRule }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/cug-tier-chain-rules`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function deleteGHACugTierChainRule(id: string): Promise<{ status: number; message: string }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/connections/google/cug-tier-chain-rules/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function getTaxEstimation(
  signal?: AbortSignal,
  propertyId?: string,
  currency = 'USD',
): Promise<TaxEstimationResponse> {
  const response = await request(
    `${getBaseURL()}/connections/google/tax-estimation${propertyId ? `?${qs.stringify({ propertyId, currency })}` : ''}`,
    {
      method: 'GET',
      signal,
      returnResponse: true,
    },
  );
  // This read-only endpoint requires exactly 200, including when another 2xx is returned.
  if (response.status !== 200) {
    throw new ApiError({
      name: 'TaxEstimationError',
      message: 'Unable to load tax estimates.',
      status: response.status,
    });
  }
  return response.json();
}

export async function updateTaxEstimationControl(
  settings: TaxEstimationControlUpdate,
): Promise<{ result: TaxEstimationControl }> {
  return request(`${getBaseURL()}/connections/google/tax-estimation/control`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(settings),
  });
}

export async function getTaxEstimationControl(signal?: AbortSignal): Promise<{ result: TaxEstimationControl }> {
  return request(`${getBaseURL()}/connections/google/tax-estimation/control`, { method: 'GET', signal });
}
